import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldCheck, CheckCircle2, AlertTriangle, Clock, Loader2, RefreshCw, Search, Zap } from 'lucide-react';
import { getTranslationSummary, getTranslateV2Status, validateTranslationV2 } from '@/services/api/funnel';
import { useToast } from '@/hooks/use-toast';

interface ValidateTranslationModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    funnelId: number;
    funnelName: string;
}

type Phase = 'check' | 'running' | 'done';

// Engine V2: um único run_id cobre todos os idiomas selecionados (a V2
// processa múltiplos idiomas dentro de UMA task/run) — diferente da V1, que
// disparava 1 task por idioma e exigia Promise.allSettled sobre vários
// task_ids. "partial" é distinguido explicitamente de "completed" (bug
// conhecido da V1: hoje ela não diferencia os dois estados na UI).
type RunStatus = 'queued' | 'running' | 'completed' | 'partial' | 'failed' | 'cancelled' | 'skipped' | 'not_found';

interface RunProgress {
    status: RunStatus;
    pagesDone: number;
    pagesTotal: number;
}

const STATUS_ICON = {
    completed: <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />,
    partial: <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />,
    pending: <Clock className="w-4 h-4 text-red-400 shrink-0" />,
};

const STATUS_LABEL = {
    completed: 'Completo',
    partial: 'Parcial',
    pending: 'Pendente',
};

const STATUS_ROW_COLOR = {
    completed: 'bg-green-50 border-green-100',
    partial: 'bg-amber-50 border-amber-100',
    pending: 'bg-red-50 border-red-100',
};

export default function ValidateTranslationModal({
    open,
    onOpenChange,
    funnelId,
    funnelName,
}: ValidateTranslationModalProps) {
    const { toast } = useToast();
    const [phase, setPhase] = useState<Phase>('check');
    const [fixResult, setFixResult] = useState<{ ok: number; retranslating: number } | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [runId, setRunId] = useState<string | null>(null);
    const [runProgress, setRunProgress] = useState<RunProgress | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const { data: summary, isLoading, refetch } = useQuery({
        queryKey: ['translation-summary', funnelId],
        queryFn: () => getTranslationSummary(funnelId),
        enabled: open,
        staleTime: 0,
    });

    const divergent = summary?.pages.filter(p => p.status !== 'completed') ?? [];
    const allOk = divergent.length === 0 && (summary?.pages.length ?? 0) > 0;

    const filteredPages = useMemo(() => {
        if (!summary) return [];
        const term = searchTerm.trim().toLowerCase();
        if (!term) return summary.pages;
        return summary.pages.filter(page =>
            (page.page_name ?? '').toLowerCase().includes(term) ||
            page.page_id.toLowerCase().includes(term)
        );
    }, [summary, searchTerm]);

    const allFilteredSelected = filteredPages.length > 0 && filteredPages.every(p => selectedIds.has(p.page_id));
    const someFilteredSelected = filteredPages.some(p => selectedIds.has(p.page_id)) && !allFilteredSelected;

    function handleToggleAll() {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredPages.forEach(p => next.delete(p.page_id));
            } else {
                filteredPages.forEach(p => next.add(p.page_id));
            }
            return next;
        });
    }

    function handleToggleRow(pageId: string) {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(pageId) ? next.delete(pageId) : next.add(pageId);
            return next;
        });
    }

    const fixMutation = useMutation({
        mutationFn: ({ pageIds, force }: { pageIds?: string[]; force?: boolean }) =>
            validateTranslationV2(funnelId, pageIds, force),
        onSuccess: (data, variables) => {
            // V1 retornava ok/retranslating calculados de antemão (checou o
            // summary antes de disparar). A V2 não faz essa checagem prévia no
            // disparo — dispara direto e deixa a task decidir o que já está
            // atual (skip) via _page_translation_is_current, ou força tudo se
            // force=true. Aproximamos "ok"/"retranslating" a partir do summary
            // já carregado (mesma fonte que a tela usa para listar as páginas),
            // antes do resultado real do run chegar via polling.
            const scopedIds = variables.pageIds && variables.pageIds.length > 0
                ? new Set(variables.pageIds)
                : null;
            const scopedPages = scopedIds
                ? (summary?.pages ?? []).filter(p => scopedIds.has(p.page_id))
                : (summary?.pages ?? []);
            const ok = variables.force ? 0 : scopedPages.filter(p => p.status === 'completed').length;
            const retranslating = scopedPages.length - ok;
            setFixResult({ ok, retranslating });

            if (!data.run_id) {
                // Disparo falhou silenciosamente no backend (não deveria
                // acontecer — controller levanta 502 nesse caso) ou nada para
                // traduzir; vai direto pro resultado final.
                setPhase('done');
                return;
            }

            setRunId(data.run_id);
            setRunProgress(null);
            setPhase('running');
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao validar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Polling de um único run_id — a V2 processa todos os idiomas
    // selecionados dentro da mesma task, então não há mais necessidade de
    // Promise.allSettled sobre vários task_ids (era 1 por idioma na V1).
    useEffect(() => {
        if (phase !== 'running' || !runId) return;

        const poll = async () => {
            try {
                const data = await getTranslateV2Status(runId);
                setRunProgress({
                    status: data.status,
                    pagesDone: parseInt(String(data.pages_done ?? '0'), 10) || 0,
                    pagesTotal: parseInt(String(data.pages_total ?? '0'), 10) || 0,
                });

                const stillPending = data.status === 'running' || data.status === 'queued' || data.status === 'not_found';
                if (!stillPending) {
                    clearInterval(pollRef.current!);
                    setPhase('done');
                }
            } catch {
                // erro de rede na consulta: segue tentando
            }
        };

        poll(); // imediato
        pollRef.current = setInterval(poll, 3000);
        return () => clearInterval(pollRef.current!);
    }, [phase, runId]);

    const aggregatedDone = runProgress?.pagesDone ?? 0;
    const aggregatedTotal = runProgress?.pagesTotal ?? 0;
    const aggregatedPct = aggregatedTotal > 0 ? Math.round((aggregatedDone / aggregatedTotal) * 100) : 0;
    // Distingue "partial" de "completed"/"failed" explicitamente (bug conhecido
    // da V1 corrigido aqui): partial = concluiu mas parte dos idiomas falhou.
    const runFailed = runProgress?.status === 'failed';
    const runPartial = runProgress?.status === 'partial';

    const handleClose = () => {
        if (fixMutation.isPending || phase === 'running') return;
        clearInterval(pollRef.current!);
        setPhase('check');
        setFixResult(null);
        setSelectedIds(new Set());
        setSearchTerm('');
        setRunId(null);
        setRunProgress(null);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => {
            if (!v && phase === 'running') return; // bloqueia fechar durante a retradução
            handleClose();
        }}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-cyan-500" />
                        Validar Tradução
                    </DialogTitle>
                </DialogHeader>

                {isLoading && (
                    <div className="flex items-center justify-center py-10 gap-3 text-gray-400">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm">Verificando traduções...</span>
                    </div>
                )}

                {!isLoading && summary && phase === 'check' && (
                    <div className="space-y-4 mt-1">
                        <p className="text-sm text-gray-500">
                            Cobertura de tradução das páginas de{' '}
                            <span className="font-medium text-gray-700">"{funnelName}"</span>.
                        </p>

                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Buscar página..."
                                className="pl-9"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {filteredPages.length > 0 && (
                            <div className="flex items-center gap-2 px-1">
                                <Checkbox
                                    checked={allFilteredSelected}
                                    data-state={someFilteredSelected ? 'indeterminate' : undefined}
                                    onCheckedChange={handleToggleAll}
                                    aria-label="Selecionar todas"
                                />
                                <span className="text-xs text-gray-500">
                                    {selectedIds.size > 0
                                        ? `${selectedIds.size} selecionada${selectedIds.size > 1 ? 's' : ''}`
                                        : 'Selecionar todas'}
                                </span>
                            </div>
                        )}

                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                            {filteredPages.map(page => (
                                <div
                                    key={page.page_id}
                                    className={`flex items-center gap-3 border rounded-lg px-3 py-2 ${STATUS_ROW_COLOR[page.status]}`}
                                >
                                    <Checkbox
                                        checked={selectedIds.has(page.page_id)}
                                        onCheckedChange={() => handleToggleRow(page.page_id)}
                                        aria-label={`Selecionar ${page.page_name || page.page_id}`}
                                    />
                                    {STATUS_ICON[page.status]}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate">
                                            {page.page_name || page.page_id}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {page.translated_nodes}/{page.total_nodes} nodes
                                            {page.language ? ` · ${page.language}` : ''}
                                        </p>
                                    </div>
                                    <span className={`text-xs font-medium shrink-0 ${
                                        page.status === 'completed' ? 'text-green-600' :
                                        page.status === 'partial' ? 'text-amber-600' : 'text-red-500'
                                    }`}>
                                        {STATUS_LABEL[page.status]}
                                    </span>
                                </div>
                            ))}
                            {filteredPages.length === 0 && (
                                <p className="text-sm text-gray-400 text-center py-4">
                                    Nenhuma página encontrada.
                                </p>
                            )}
                        </div>

                        {allOk ? (
                            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5 text-sm text-green-700">
                                <CheckCircle2 className="w-4 h-4 shrink-0" />
                                Todas as páginas estão com tradução completa.
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-sm text-amber-700">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                {divergent.length} página{divergent.length > 1 ? 's' : ''} com divergência — clique em "Corrigir" para retraduzir.
                            </div>
                        )}

                        <div className="flex justify-between items-center pt-1 border-t gap-2">
                            <button
                                type="button"
                                onClick={() => refetch()}
                                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                            >
                                <RefreshCw className="w-3 h-3" />
                                Atualizar
                            </button>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={handleClose}>
                                    Fechar
                                </Button>
                                {!allOk && (
                                    <Button
                                        onClick={() => fixMutation.mutate({
                                            pageIds: selectedIds.size > 0 ? Array.from(selectedIds) : undefined,
                                        })}
                                        disabled={fixMutation.isPending}
                                        title="Retraduz apenas as páginas ainda não concluídas"
                                        className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2"
                                    >
                                        {fixMutation.isPending ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Corrigindo...
                                            </>
                                        ) : selectedIds.size > 0 ? (
                                            <>
                                                <ShieldCheck className="w-4 h-4" />
                                                Retraduzir {selectedIds.size} selecionada{selectedIds.size > 1 ? 's' : ''}
                                            </>
                                        ) : (
                                            <>
                                                <ShieldCheck className="w-4 h-4" />
                                                Corrigir {divergent.length} página{divergent.length > 1 ? 's' : ''}
                                            </>
                                        )}
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    onClick={() => fixMutation.mutate({
                                        pageIds: selectedIds.size > 0 ? Array.from(selectedIds) : undefined,
                                        force: true,
                                    })}
                                    disabled={fixMutation.isPending}
                                    title={selectedIds.size > 0
                                        ? "Retraduz as páginas selecionadas mesmo já marcadas como concluídas"
                                        : "Retraduz TODAS as páginas do funil, mesmo já marcadas como concluídas"}
                                    className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-2"
                                >
                                    <Zap className="w-4 h-4" />
                                    {selectedIds.size > 0
                                        ? `Forçar ${selectedIds.size} selecionada${selectedIds.size > 1 ? 's' : ''}`
                                        : 'Forçar todas'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {phase === 'running' && (
                    <div className="space-y-5 mt-2">
                        <div className="flex items-center gap-3">
                            <Loader2 className="w-5 h-5 animate-spin text-cyan-500 shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-gray-800">Retraduzindo páginas...</p>
                                <p className="text-xs text-gray-400 mt-0.5">Não feche esta janela durante a retradução.</p>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex justify-between text-xs text-gray-500">
                                <span>Progresso</span>
                                <span>{aggregatedDone} de {aggregatedTotal} página{aggregatedTotal !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                                <div
                                    className="bg-cyan-500 h-2.5 rounded-full transition-all duration-500"
                                    style={{ width: `${aggregatedPct}%` }}
                                />
                            </div>
                            <div className="flex justify-between text-xs text-gray-400">
                                <span>{aggregatedPct}%</span>
                            </div>
                        </div>
                    </div>
                )}

                {phase === 'done' && fixResult && (
                    <div className="space-y-4 mt-1">
                        <div className="flex flex-col items-center gap-3 py-4 text-center">
                            {runFailed ? (
                                <AlertTriangle className="w-12 h-12 text-red-500" />
                            ) : runPartial ? (
                                <AlertTriangle className="w-12 h-12 text-amber-500" />
                            ) : (
                                <CheckCircle2 className="w-12 h-12 text-green-500" />
                            )}
                            <div>
                                <p className="text-base font-semibold text-gray-800">
                                    {runId
                                        ? (runFailed
                                            ? 'Falha na retradução'
                                            : runPartial
                                                ? 'Retradução parcialmente concluída'
                                                : 'Retradução concluída!')
                                        : 'Correção enfileirada!'}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    {fixResult.ok} página{fixResult.ok !== 1 ? 's' : ''} já OK.
                                    {fixResult.retranslating > 0 && (
                                        <span className="block mt-0.5">
                                            {fixResult.retranslating} página{fixResult.retranslating > 1 ? 's' : ''} {runId ? 'retraduzida' : 'sendo retraduzida'}{fixResult.retranslating > 1 ? 's' : ''}{!runId ? ' agora.' : '.'}
                                        </span>
                                    )}
                                    {runFailed && (
                                        <span className="block mt-1 text-red-500">
                                            A retradução falhou. Tente novamente.
                                        </span>
                                    )}
                                    {runPartial && (
                                        <span className="block mt-1 text-amber-600">
                                            Parte dos idiomas falhou na retradução — revise as páginas pendentes.
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end pt-1 border-t">
                            <Button onClick={handleClose} className="bg-[#0f172a] hover:bg-[#0a1120] text-white">
                                Fechar
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
